import React, { lazy, Suspense } from "react";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANG_CODES, getLangFromPath, getPathWithLang } from './i18n';
import ErrorBoundary from "./components/ErrorBoundary";
import { Footer } from "./components/Footer";
import { ChampionshipBanner } from "./components/ChampionshipBanner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { GameSettingsProvider } from "./contexts/GameSettingsContext";
import { GameStateProvider } from "./contexts/GameStateContext";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import { StealthGateway } from "./components/StealthGateway";
import { captureIncomingReferral, getReferralId } from "./components/AmbassadorService";
import { AmbassadorToastService, UnlockAnimationOverlay } from "./components/AmbassadorToastService";
import { AchievementToast } from "./components/AchievementToast";
import { useTipJarInjector } from "./hooks/useTipJarInjector";
import { useNightModeSync } from "./hooks/useNightModeSync";
import { useTipJarMutationObserver } from "./hooks/useTipJarMutationObserver";
import { useChampionshipToggleInjector } from "./hooks/useChampionshipToggleInjector";
import { useChampionBadgeInjector } from "./hooks/useChampionBadgeInjector";
import { useHreflang } from './hooks/useHreflang';

// Lazy-load all non-core routes for code splitting
const Tips = lazy(() => import("./pages/Tips"));
const History = lazy(() => import("./pages/History"));
const HowToPlay = lazy(() => import("./pages/HowToPlay"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Embed = lazy(() => import("./pages/Embed"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Features = lazy(() => import("./pages/Features"));
const Search = lazy(() => import("./pages/Search"));
const DailyChallenge = lazy(() => import("./pages/DailyChallenge"));
const Replays = lazy(() => import("./pages/Replays"));
const Shop = lazy(() => import("./pages/Shop"));
const Cosmetics = lazy(() => import("./pages/Cosmetics"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminSeo = lazy(() => import("./pages/AdminSeo"));

// Variant game hub pages
const VariantFast = lazy(() => import("./pages/variants/FastMode"));
const VariantNight = lazy(() => import("./pages/variants/NightMode"));
const VariantHard = lazy(() => import("./pages/variants/HardMode"));
const VariantHighSpeed = lazy(() => import("./pages/variants/HighSpeed"));
const VariantDarkMode = lazy(() => import("./pages/variants/DarkMode"));
const VariantNoObstacles = lazy(() => import("./pages/variants/NoObstacles"));
const HallOfLegends = lazy(() => import("./pages/HallOfLegends"));
const TrophyCase = lazy(() => import("./pages/TrophyCase"));
const Museum = lazy(() => import("./pages/Museum"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Contact = lazy(() => import("./pages/Contact"));
const Unblocked = lazy(() => import("./pages/Unblocked"));
const About = lazy(() => import("./pages/About"));

// Minimal loading fallback (Chrome-native style)
const RouteLoader = () => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: '50vh',
    fontFamily: 'system-ui, -apple-system, Roboto, Arial, sans-serif',
    fontSize: '13px',
    color: '#666'
  }}>
    Loading...
  </div>
);
// Language redirect: detect browser language and redirect to /xx/ on first visit
function LanguageRedirect() {
  const [location, setLocation] = useLocation();
  
  useEffect(() => {
    // Only redirect if on root path with no language prefix
    const currentLang = getLangFromPath(location);
    if (currentLang !== 'en') return; // Already on a language path
    if (location !== '/') return; // Only redirect from root
    
    // Check if user has a stored language preference
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang && storedLang !== 'en' && SUPPORTED_LANG_CODES.includes(storedLang)) {
      setLocation(getPathWithLang('/', storedLang));
      return;
    }
    
    // Detect browser language
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    if (browserLang && browserLang !== 'en' && SUPPORTED_LANG_CODES.includes(browserLang)) {
      // Only redirect if no previous visit (no localStorage key)
      if (!localStorage.getItem('cdg_lang_redirected')) {
        localStorage.setItem('cdg_lang_redirected', '1');
        localStorage.setItem('i18nextLng', browserLang);
        setLocation(getPathWithLang('/', browserLang));
      }
    }
  }, [location, setLocation]);
  
  return null;
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  const [location] = useLocation();
  const hidePublicFooter = /^\/(?:admin|embed)(?:\/|$)/.test(location);
  return (
    <>
      <Suspense fallback={<RouteLoader />}>
        <LanguageRedirect />
        <Switch>
        {/* Language-prefixed routes for all 25 supported languages */}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={lang} path={`/${lang}`} component={Home} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-tips`} path={`/${lang}/tips`} component={Tips} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-history`} path={`/${lang}/history`} component={History} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-htp`} path={`/${lang}/how-to-play`} component={HowToPlay} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-lb`} path={`/${lang}/leaderboard`} component={Leaderboard} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-blog`} path={`/${lang}/blog`} component={Blog} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-blogpost`} path={`/${lang}/blog/:slug`} component={BlogPost} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-faq`} path={`/${lang}/faq`} component={FAQ} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-museum`} path={`/${lang}/museum`} component={Museum} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-hol`} path={`/${lang}/hall-of-legends`} component={HallOfLegends} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-contact`} path={`/${lang}/contact`} component={Contact} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-privacy`} path={`/${lang}/privacy-policy`} component={PrivacyPolicy} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-terms`} path={`/${lang}/terms-of-service`} component={TermsOfService} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-unblocked`} path={`/${lang}/unblocked`} component={Unblocked} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-about`} path={`/${lang}/about`} component={About} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-features`} path={`/${lang}/features`} component={Features} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-dc`} path={`/${lang}/daily-challenge`} component={DailyChallenge} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-replays`} path={`/${lang}/replays`} component={Replays} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-shop`} path={`/${lang}/shop`} component={Shop} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-cosmetics`} path={`/${lang}/cosmetics`} component={Cosmetics} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-achievements`} path={`/${lang}/achievements`} component={Achievements} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-trophy`} path={`/${lang}/trophy-case`} component={TrophyCase} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-vfast`} path={`/${lang}/variants/fast`} component={VariantFast} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-vnight`} path={`/${lang}/variants/night`} component={VariantNight} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-vhard`} path={`/${lang}/variants/hard`} component={VariantHard} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-vhs`} path={`/${lang}/variants/high-speed`} component={VariantHighSpeed} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-vdm`} path={`/${lang}/variants/dark-mode`} component={VariantDarkMode} />
        ))}
        {SUPPORTED_LANG_CODES.filter(l => l !== 'en').map(lang => (
          <Route key={`${lang}-vno`} path={`/${lang}/variants/no-obstacles`} component={VariantNoObstacles} />
        ))}
        {/* English routes (default) */}
        <Route path="/" component={Home} />
        <Route path="/tips" component={Tips} />
        <Route path="/history" component={History} />
        <Route path="/how-to-play" component={HowToPlay} />
        <Route path="/leaderboard" component={Leaderboard} />
        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/faq" component={FAQ} />
        <Route path="/features" component={Features} />
        <Route path="/search" component={Search} />
        <Route path="/daily-challenge" component={DailyChallenge} />
        <Route path="/replays" component={Replays} />
        <Route path="/shop" component={Shop} />
        <Route path="/cosmetics" component={Cosmetics} />
        <Route path="/unblocked" component={Unblocked} />
        <Route path="/achievements" component={Achievements} />
        <Route path="/admin" component={Admin} />
        <Route path="/admin/seo" component={AdminSeo} />
        <Route path="/embed" component={Embed} />
        {/* Variant game hub pages — SEO-safe with canonical tags */}
        <Route path="/variants/fast" component={VariantFast} />
        <Route path="/variants/night" component={VariantNight} />
        <Route path="/variants/hard" component={VariantHard} />
        <Route path="/variants/high-speed" component={VariantHighSpeed} />
        <Route path="/variants/dark-mode" component={VariantDarkMode} />
        <Route path="/variants/no-obstacles" component={VariantNoObstacles} />
        <Route path="/hall-of-legends" component={HallOfLegends} />
        <Route path="/trophy-case" component={TrophyCase} />
        <Route path="/museum" component={Museum} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/contact" component={Contact} />
        <Route path="/about" component={About} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
        </Switch>
      </Suspense>
      {!hidePublicFooter && <Footer />}
    </>
  );
}

// RuntimeInjectors must render INSIDE the providers so context hooks resolve correctly.
// This was the cause of the v2.7.1 blank-screen crash:
//   `useNightModeSync()` calls `useGameSettings()` which requires GameSettingsProvider.
function RuntimeInjectors() {
  // Sync Night Mode with GameSettingsContext toggle
  useNightModeSync();

  // Monitor SystemMenu and inject TipJar via MutationObserver
  useTipJarMutationObserver();

  // Inject Championship toggle into SystemMenu
  useChampionshipToggleInjector();

  // Inject Champion status badge
  useChampionBadgeInjector();

  // Inject hreflang tags for all 25 supported languages
  useHreflang();

  // Legacy TipJar injector (kept for backward compatibility)
  useTipJarInjector({
    tipJarUrl: 'https://tiptopjar.com/chromedinogame',
  });

  // Capture incoming UTM referrals globally on app load
  useEffect(() => {
    getReferralId();
    captureIncomingReferral();
  }, []);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
        <GameSettingsProvider>
          <GameStateProvider>
            <ThemeProvider defaultTheme={(() => { try { const s = localStorage.getItem('gameSettings'); if (s) { const p = JSON.parse(s); return p.themeMode === 'dark' ? 'dark' : 'light'; } } catch {} return 'light'; })()}>
              <TooltipProvider>
                <Toaster />
                <RuntimeInjectors />
                <div className="min-h-screen flex flex-col bg-background text-foreground">
                  <ChampionshipBanner />
                  <Router />
                  <StealthGateway />
                  <AmbassadorToastService />
                  <UnlockAnimationOverlay />
                  <AchievementToast />
                </div>
              </TooltipProvider>
            </ThemeProvider>
          </GameStateProvider>
        </GameSettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
